import { memo, type PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef } from 'react';

import { usePlayerSeekChange, usePlayerStore } from '@air/component-player';
import { formatDuration } from '@air/utilities';
import { tailwindVariants } from '@air/utils-tailwind';

import { useAnnotationV2Store } from '~/components/AnnotationsV2/providers/AnnotationV2Provider';
import { type CommentRangeValue } from '~/components/v2/components/asset-modal/visualizer/video/getCommentRangePercentStyle';
import { SEEK_THROTTLE_MS } from '~/components/v2/components/asset-modal/visualizer/video/VideoCommentRangeSelector/constants';
import { type CommentRangeHandleSide } from '~/components/v2/components/asset-modal/visualizer/video/VideoCommentRangeSelector/types';

const videoCommentRangeSelectorHandle = tailwindVariants({
  base: 'pointer-events-auto absolute inset-y-0 z-20 flex w-3 cursor-ew-resize items-center justify-center ads:bg-ads-gray-12 bg-gray-12',
  variants: {
    side: {
      left: 'left-0',
      right: 'right-0',
    },
  },
});

type CommentRangeContainerRef = {
  readonly current: HTMLDivElement | null | undefined;
};

type VideoCommentRangeSelectorHandleProps = {
  containerRef: CommentRangeContainerRef;
  endTime: number;
  onDragEnd: (range: CommentRangeValue) => void;
  onDragStart: () => void;
  side: CommentRangeHandleSide;
  startTime: number;
};

export const VideoCommentRangeSelectorHandle = memo(
  ({ containerRef, endTime, onDragEnd, onDragStart, side, startTime }: VideoCommentRangeSelectorHandleProps) => {
    const duration = usePlayerStore((state) => state.duration);
    const playing = usePlayerStore((state) => state.playing);
    const videoElement = usePlayerStore((state) => state.videoElement);
    const setCurrentTime = usePlayerStore((state) => state.setCurrentTime);
    const { onPlayerSeekStart, onPlayerSeekEnd } = usePlayerSeekChange();
    const setCommentRange = useAnnotationV2Store((state) => state.setCommentRange);
    const activeDragCleanupRef = useRef<(() => void) | null>(null);

    useEffect(() => {
      return () => {
        activeDragCleanupRef.current?.();
        activeDragCleanupRef.current = null;
      };
    }, []);

    const handlePointerDown = useCallback(
      (event: ReactPointerEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);

        const container = containerRef.current;

        if (!container) {
          return;
        }

        const timelineWidth = container.getBoundingClientRect().width;

        if (timelineWidth <= 0) {
          return;
        }

        onDragStart();

        activeDragCleanupRef.current?.();

        const shouldSyncPlayhead = !playing;
        const initialTime = side === 'left' ? startTime : endTime;

        if (shouldSyncPlayhead) {
          onPlayerSeekStart();
          setCurrentTime(initialTime);
          if (videoElement) {
            videoElement.currentTime = initialTime;
          }
        }

        const secondsPerPixel = duration / timelineWidth;
        const initialClientX = event.clientX;
        let lastSeekTime = 0;
        let lastClampedTime = initialTime;
        let lastRange = { startTime, endTime };

        const handlePointerMove = (moveEvent: PointerEvent) => {
          const deltaX = moveEvent.clientX - initialClientX;
          const newTime = Math.max(0, Math.min(initialTime + deltaX * secondsPerPixel, duration));

          let clampedTime: number;
          let nextRange: CommentRangeValue;

          if (side === 'left') {
            clampedTime = Math.max(0, Math.min(newTime, endTime));
            nextRange = { startTime: clampedTime, endTime };
          } else {
            clampedTime = Math.min(duration, Math.max(newTime, startTime));
            nextRange = { startTime, endTime: clampedTime };
          }

          setCommentRange(nextRange);
          lastClampedTime = clampedTime;
          lastRange = nextRange;

          if (shouldSyncPlayhead) {
            setCurrentTime(clampedTime);

            const now = performance.now();

            if (videoElement && now - lastSeekTime >= SEEK_THROTTLE_MS) {
              videoElement.currentTime = clampedTime;
              lastSeekTime = now;
            }
          }
        };

        const cleanupPointerDrag = () => {
          window.removeEventListener('pointermove', handlePointerMove);
          window.removeEventListener('pointerup', handlePointerUp);

          if (activeDragCleanupRef.current === cleanupPointerDrag) {
            activeDragCleanupRef.current = null;
          }

          if (shouldSyncPlayhead) {
            onPlayerSeekEnd();
          }
        };

        const handlePointerUp = () => {
          if (shouldSyncPlayhead && videoElement) {
            videoElement.currentTime = lastClampedTime;
          }

          cleanupPointerDrag();
          onDragEnd(lastRange);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        activeDragCleanupRef.current = cleanupPointerDrag;
      },
      [
        containerRef,
        duration,
        endTime,
        onDragEnd,
        onDragStart,
        onPlayerSeekEnd,
        onPlayerSeekStart,
        playing,
        setCommentRange,
        setCurrentTime,
        side,
        startTime,
        videoElement,
      ],
    );

    const currentValue = side === 'left' ? startTime : endTime;
    const valueMax = side === 'left' ? endTime : duration;
    const valueMin = side === 'left' ? 0 : startTime;

    return (
      <button
        aria-label={side === 'left' ? 'Adjust start time' : 'Adjust end time'}
        aria-orientation="horizontal"
        aria-valuemax={valueMax}
        aria-valuemin={valueMin}
        aria-valuenow={currentValue}
        aria-valuetext={formatDuration(currentValue)}
        className={videoCommentRangeSelectorHandle({ side })}
        onPointerDown={handlePointerDown}
        role="slider"
        type="button"
      >
        <div className="h-3.5 w-1 rounded-full ads:bg-ads-gray-1 bg-gray-1" />
      </button>
    );
  },
);

VideoCommentRangeSelectorHandle.displayName = 'VideoCommentRangeSelectorHandle';
